<template>
  <div>
    <div class="columns">
      <div class="column is-one-fifth">
        <p class="has-text-weight-bold is-size-6 mb-sm">Questions &amp; Answers</p>
        <p class="has-text-grey is-size-7 mb-md">
          Manage the content suppliers see in the Supplier Assistant.
          Live entries are visible immediately; drafts stay hidden until published.
        </p>
        <b-button type="is-primary rounded-lg" expanded :disabled="!ready" @click="add">Add Q&amp;A</b-button>
        <b-message v-if="optionsError" type="is-danger" size="is-small" class="mt-md">
          The assistant groups &amp; categories could not be loaded, so entries can't be added or filtered.
          <a href="#" @click.prevent="loadOptions">Retry</a>
        </b-message>
      </div>
      <div class="column">
        <!-- Filters Toolbar -->
        <div class="has-background-white rounded-lg pa-md mb-xxs">
          <div class="columns">
            <div class="column">
              <EntryFilters
                v-if="filtersLoaded && ready"
                :applied-filters="filters"
                :groups="groups"
                :categories="categories"
                @filter="filter" />
            </div>
            <div class="column is-narrow">
              <b-select v-model="orderBy" size="is-medium" expanded @input="sort">
                <option value="-updated_at">Recently Updated</option>
                <option value="updated_at">Least Recently Updated</option>
                <option value="title">Title (A-Z)</option>
                <option value="-title">Title (Z-A)</option>
                <option value="-id">Newest</option>
                <option value="id">Oldest</option>
              </b-select>
            </div>
          </div>
          <!-- Status pills -->
          <div class="columns is-vcentered is-mobile">
            <div class="column is-narrow">
              <div class="buttons has-addons is-marginless">
                <b-button
                  v-for="pill in statusPills"
                  :key="pill.value"
                  size="is-small"
                  rounded
                  :type="statusFilter === pill.value ? 'is-primary' : 'is-light'"
                  @click="setStatus(pill.value)">
                  {{ pill.label }}
                </b-button>
              </div>
            </div>
            <div class="column has-text-right is-size-7 has-text-grey">
              <span v-if="!loading">{{ total }} {{ total === 1 ? 'entry' : 'entries' }}</span>
            </div>
          </div>
        </div>
        <!-- Results -->
        <div class="has-background-white rounded-lg">
          <b-table :data="results" :mobile-cards="false" :loading="loading" hoverable class="rounded-lg is-size-7">
            <template>
              <!-- Question -->
              <b-table-column label="Question" v-slot="props">
                <a href="#" class="has-text-weight-bold has-text-info" @click.prevent="edit(props.row)">{{ props.row.title }}</a>
                <p class="has-text-grey-light">{{ props.row.slug }}</p>
              </b-table-column>
              <!-- Group -->
              <b-table-column label="Group" cell-class="nowrap" v-slot="props">
                {{ props.row.group ? props.row.group.name : '' }}
              </b-table-column>
              <!-- Categories -->
              <b-table-column label="Categories" v-slot="props">
                <b-taglist v-if="props.row.categories.length">
                  <b-tag v-for="category in props.row.categories" :key="category.id" size="is-small">{{ category.label }}</b-tag>
                </b-taglist>
                <span v-else class="has-text-grey-light">Search only</span>
              </b-table-column>
              <!-- Status -->
              <b-table-column label="Status" cell-class="nowrap" width="96" centered v-slot="props">
                <b-tag rounded :type="props.row.status === 'live' ? 'is-success' : 'is-warning'">
                  {{ props.row.status === 'live' ? 'Live' : 'Draft' }}
                </b-tag>
              </b-table-column>
              <!-- Updated -->
              <b-table-column label="Updated" cell-class="nowrap" width="160" v-slot="props">
                <p>{{ $dates.utc(props.row.updated_at).local().format('MMM D, YYYY') }}</p>
                <p v-if="props.row.updated_by" class="has-text-grey-light">{{ props.row.updated_by.name }}</p>
              </b-table-column>
              <!-- Actions -->
              <b-table-column label="" cell-class="nowrap" width="64" centered v-slot="props">
                <a href="#" @click.prevent="edit(props.row)">Edit</a>
              </b-table-column>
            </template>
            <template #empty>
              <div class="py-xxl has-text-grey has-text-centered">
                {{ (loading) ? 'Loading data...' : 'No Q&A entries match these filters.' }}
              </div>
            </template>
          </b-table>
          <div class="px-md pb-md">
            <b-pagination
              :total="total"
              :current.sync="currentPage"
              :rounded="true"
              :per-page="perPage"
              @change="paginate">
            </b-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <b-modal
      :active.sync="showModal"
      has-modal-card
      :width="1100"
      :can-cancel="['escape', 'x']"
      @close="entry = null">
      <EntryModal
        v-if="showModal"
        :entry="entry"
        :groups="groups"
        :categories="categories"
        @updated="changed"
        @deleted="changed" />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Filterable from '@/mixins/Filterable'
import EntryFilters from './EntryFilters'
import EntryModal from './EntryModal'

export default {
  components: {
    EntryFilters,
    EntryModal
  },
  computed: {
    ...mapGetters(['api', 'assistantOptions']),
    groups () {
      return this.assistantOptions.groups || []
    },
    categories () {
      return this.assistantOptions.categories || []
    },
    optionsError () {
      return this.assistantOptions.error
    },
    endpoint () {
      return this.api.baseUrl + '/v1/assistant/entries/'
    },
    ready () {
      return this.assistantOptions.groups !== null
    },
    // Mirror the applied status facet onto the quick pills.
    statusFilter () {
      const applied = (this.filters.facets && this.filters.facets.status) || []
      if (applied.length === 1) return applied[0].value
      return 'all'
    }
  },
  data () {
    return {
      key: 'settings.assistant-qa',
      orderBy: '-updated_at',
      perPage: 25,
      showModal: false,
      entry: null,
      statusPills: [
        { value: 'all', label: 'All' },
        { value: 'live', label: 'Live' },
        { value: 'draft', label: 'Draft' }
      ]
    }
  },
  methods: {
    add () {
      this.entry = null
      this.showModal = true
    },
    edit (entry) {
      // Without groups/categories the form can't render; the error banner
      // (with Retry) explains the state.
      if (!this.ready) return
      this.entry = entry
      this.showModal = true
    },
    setStatus (value) {
      const filters = JSON.parse(JSON.stringify(this.filters))
      filters.facets = filters.facets || {}
      if (value === 'all') {
        delete filters.facets.status
      } else {
        const pill = this.statusPills.find(pill => pill.value === value)
        filters.facets.status = [{ label: pill.label, value: pill.value }]
      }
      filters.preset = 'custom'
      this.filter(filters)
    },
    // Any create/edit/delete: reload the list and refresh the chatbot's cached
    // config so it doesn't keep serving stale (or now-draft) content.
    changed () {
      this.load()
      this.refreshChatbotConfig()
    },
    // The chatbot renders the config from the store (store/assistant.js), so
    // committing the refreshed copy updates an already-open chatbot instantly.
    refreshChatbotConfig () {
      return this.$store.dispatch('refreshAssistantConfig')
    },
    loadOptions () {
      // Session-cached in the store (shared with the Missed Questions page);
      // dispatching again after a failure retries.
      return this.$store.dispatch('loadAssistantOptions')
    }
  },
  mixins: [Filterable],
  mounted () {
    this.loadOptions()
    this.load()
  }
}
</script>
